import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import {
  useTrackChangedCustomFieldDateValue,
  useTrackChangedCustomFieldPlainTextValue,
  useTrackChangedCustomFieldValue,
  useTrackChangedCustomFieldValues,
} from '@air/analytics';
import { CustomFields } from '@air/api';
import {
  CustomFieldTypeName,
  type CustomFieldValue,
  type ResponseCustomFieldValue,
  type TaskData,
} from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { AssetCustomFieldValueUpdateTasksPanel } from '~/components/AssetCustomFieldValueUpdateTasks/AssetCustomFieldValueUpdateTasksPanel';
import { getCustomFieldOptionsKey } from '~/constants/react-query-keys';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useReloadApplication } from '~/providers/ReloadApplicationProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeAssetCustomFieldValueUpdateTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { type AssetCustomFieldValueUpdateTask } from '~/store/tasks/types';
import { ReadyState } from '~/types/sockets';
import { updateClipCustomFieldsWithValue } from '~/utils/CustomFields';
import { useUpdateAssetsInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

type UpdateParams = {
  nextValue?: CustomFieldValue | null;
  multiValuesToAdd?: CustomFieldValue[];
  multiValueIdsToRemove?: string[];
  nextPlainTextValue?: string;
  nextDateValue?: string;
};

const getUpdateParamsForCompletion = ({
  localTask,
  taskData,
  customFieldValues,
}: {
  localTask: AssetCustomFieldValueUpdateTask;
  taskData?: TaskData<'AssetCustomFieldValueUpdater'>['data'];
  customFieldValues: CustomFieldValue[];
}): UpdateParams => {
  const operation = taskData?.operation ?? localTask.metadata.operation;
  const setUpdate = taskData?.updates?.set;
  const unsetUpdate = taskData?.updates?.unset;
  const hasRemoteUpdates = !!taskData?.updates;

  switch (operation) {
    case 'singleSelect': {
      const valueId = hasRemoteUpdates ? (setUpdate?.valueId ?? null) : (localTask.metadata.valueId ?? null);

      return {
        nextValue: valueId ? customFieldValues.find((value) => value.id === valueId) || null : null,
      };
    }
    case 'multiSelect': {
      const valueIdsToAdd = hasRemoteUpdates ? (setUpdate?.valueIds ?? []) : (localTask.metadata.valueIdsToAdd ?? []);
      const valueIdsToRemove = hasRemoteUpdates
        ? (unsetUpdate?.valueIds ?? [])
        : (localTask.metadata.valueIdsToRemove ?? []);

      return {
        multiValuesToAdd: customFieldValues.filter((value) => valueIdsToAdd.includes(value.id)),
        multiValueIdsToRemove: valueIdsToRemove,
      };
    }
    case 'plainText': {
      return {
        nextPlainTextValue: hasRemoteUpdates
          ? (setUpdate?.plainTextValue ?? '')
          : (localTask.metadata.plainTextValue ?? ''),
      };
    }
    case 'date': {
      return {
        nextDateValue: hasRemoteUpdates ? setUpdate?.dateValue : (localTask.metadata.dateValue ?? undefined),
      };
    }
    default: {
      // Compile-time exhaustiveness check — TS will error here if a new operation is added without a case
      // Compile-time exhaustiveness check — TS errors if a new operation is added without a case
      const exhaustive: never = operation;
      reportErrorToBugsnag({
        error: new Error(`Unhandled custom field operation: ${operation}`),
        context: 'getUpdateParamsForCompletion',
        metadata: { operation: exhaustive },
      });
      return {};
    }
  }
};

const toCustomFieldValue = (
  value: ResponseCustomFieldValue,
  localTask: AssetCustomFieldValueUpdateTask,
  pos: number,
): CustomFieldValue => {
  return {
    ...value,
    workspaceId: localTask.metadata.baseCustomField.workspaceId,
    customFieldId: localTask.metadata.baseCustomField.id,
    pos,
  };
};

const getFallbackCustomFieldValues = (localTask: AssetCustomFieldValueUpdateTask): CustomFieldValue[] => {
  return (localTask.metadata.baseCustomField.values || []).map((value, index) =>
    toCustomFieldValue(value, localTask, index),
  );
};

export const PrivateAssetCustomFieldValueUpdateTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const tasksSelector = useMemo(
    () => makeAssetCustomFieldValueUpdateTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const dispatch = useDispatch();
  const assetCustomFieldValueUpdateTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { onAddPendingAction, onRemovePendingAction } = useReloadApplication();
  const { updateAssetsInAllViews } = useUpdateAssetsInAllViews();
  const { trackChangedCustomFieldDateValue } = useTrackChangedCustomFieldDateValue();
  const { trackChangedCustomFieldPlainTextValue } = useTrackChangedCustomFieldPlainTextValue();
  const { trackChangedCustomFieldValue } = useTrackChangedCustomFieldValue();
  const { trackChangedCustomFieldValues } = useTrackChangedCustomFieldValues();

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'AssetCustomFieldValueUpdate',
    remoteType: 'AssetCustomFieldValueUpdater',
    onComplete: async ({ task, localTask, enrichMetadata }) => {
      const assetIds = task.data?.assetIds ?? localTask.metadata.assetIds ?? [];
      const operation = task.data?.operation ?? localTask.metadata.operation;
      const hasRemoteUpdates = !!task.data?.updates;
      let customFieldValues = getFallbackCustomFieldValues(localTask);
      let unresolvedSetValueIds: string[] = [];

      if ((operation === 'singleSelect' || operation === 'multiSelect') && currentWorkspace?.id) {
        const customFieldId = localTask.metadata.baseCustomField.id;
        const optionsQueryKey = getCustomFieldOptionsKey(customFieldId);
        const cachedOptions = queryClient.getQueryData<{ data: CustomFieldValue[] }>(optionsQueryKey);

        if (cachedOptions?.data?.length) {
          customFieldValues = cachedOptions.data;
        }

        let valueIdsToSet: string[];

        if (operation === 'singleSelect') {
          valueIdsToSet = [
            hasRemoteUpdates ? task.data?.updates?.set?.valueId : (localTask.metadata.valueId ?? undefined),
          ].filter((valueId): valueId is string => !!valueId);
        } else if (hasRemoteUpdates) {
          valueIdsToSet = task.data?.updates?.set?.valueIds ?? [];
        } else {
          valueIdsToSet = localTask.metadata.valueIdsToAdd ?? [];
        }

        unresolvedSetValueIds = valueIdsToSet.filter(
          (valueId) => !customFieldValues.some((value) => value.id === valueId),
        );

        if (unresolvedSetValueIds.length > 0) {
          try {
            const optionsResponse = await queryClient.fetchQuery({
              queryKey: optionsQueryKey,
              queryFn: () =>
                CustomFields.listCustomFieldValues({
                  workspaceId: currentWorkspace.id,
                  customFieldId,
                }),
              staleTime: 0,
            });

            if (optionsResponse.data.length > 0) {
              customFieldValues = optionsResponse.data;
              unresolvedSetValueIds = valueIdsToSet.filter(
                (valueId) => !customFieldValues.some((value) => value.id === valueId),
              );
            }
          } catch (error) {
            reportErrorToBugsnag({
              error,
              context: 'Failed to resolve custom field options on task completion',
              metadata: {
                customFieldId,
                operation,
                unresolvedSetValueIds,
                localTaskId: localTask.localTaskId,
                remoteTaskId: task.id,
                workspaceId: currentWorkspace?.id,
              },
            });
          }
        }
      }
      const boardId = getBoardIdFromPath(window.location.pathname) || localTask.metadata.boardId;

      if (assetIds.length > 0) {
        const { nextValue, multiValuesToAdd, multiValueIdsToRemove, nextPlainTextValue, nextDateValue } =
          getUpdateParamsForCompletion({ localTask, taskData: task.data, customFieldValues });

        const shouldSkipSingleSelectOptimisticUpdate = operation === 'singleSelect' && unresolvedSetValueIds.length > 0;

        if (!shouldSkipSingleSelectOptimisticUpdate) {
          updateAssetsInAllViews({
            assetIdentifiers: assetIds,
            boardId: boardId || undefined,
            updateFn: (clip) => {
              const customFields = updateClipCustomFieldsWithValue({
                assetId: clip.assetId,
                customFieldValues: clip.customFields,
                baseCustomField: localTask.metadata.baseCustomField,
                nextValue,
                multiValuesToAdd,
                multiValueIdsToRemove,
                nextPlainTextValue,
                nextDateValue,
              });

              return { ...clip, customFields };
            },
          });
        }
      }

      enrichMetadata(task.data ?? {});

      if (localTask.metadata.trackLocation) {
        const customFieldId = localTask.metadata.baseCustomField.id;

        switch (operation) {
          case 'singleSelect': {
            trackChangedCustomFieldValue({
              location: localTask.metadata.trackLocation,
              customFieldId,
              valueId: hasRemoteUpdates
                ? (task.data?.updates?.set?.valueId ?? null)
                : (localTask.metadata.valueId ?? null),
            });
            break;
          }
          case 'multiSelect': {
            const valueIdsToAdd = hasRemoteUpdates
              ? (task.data?.updates?.set?.valueIds ?? [])
              : (localTask.metadata.valueIdsToAdd ?? []);
            const valueIdsToRemove = hasRemoteUpdates
              ? (task.data?.updates?.unset?.valueIds ?? [])
              : (localTask.metadata.valueIdsToRemove ?? []);

            if (valueIdsToAdd.length > 0) {
              trackChangedCustomFieldValues({
                location: localTask.metadata.trackLocation,
                customFieldId,
                valueIds: valueIdsToAdd,
                actionType: 'add',
              });
            }

            if (valueIdsToRemove.length > 0) {
              trackChangedCustomFieldValues({
                location: localTask.metadata.trackLocation,
                customFieldId,
                valueIds: valueIdsToRemove,
                actionType: 'remove',
              });
            }
            break;
          }
          case 'plainText': {
            trackChangedCustomFieldPlainTextValue({
              location: localTask.metadata.trackLocation,
              type: CustomFieldTypeName.plainText,
              customFieldId,
            });
            break;
          }
          case 'date': {
            trackChangedCustomFieldDateValue({
              location: localTask.metadata.trackLocation,
              type: CustomFieldTypeName.date,
              customFieldId,
              dateValue: hasRemoteUpdates
                ? task.data?.updates?.set?.dateValue
                : (localTask.metadata.dateValue ?? undefined),
            });
            break;
          }
        }
      }
    },
  });

  const syncExecutedForReadyStateRef = useRef(false);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  useEffect(() => {
    if (!isEmpty(assetCustomFieldValueUpdateTasks)) {
      onAddPendingAction({ id: 'asset-custom-field-value-update', description: 'Updating custom fields' });
    } else {
      onRemovePendingAction('asset-custom-field-value-update');
    }
  }, [assetCustomFieldValueUpdateTasks, onAddPendingAction, onRemovePendingAction]);

  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: assetCustomFieldValueUpdateTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (assetCustomFieldValueUpdateTasks.length < 1) {
    return null;
  }

  return (
    <AssetCustomFieldValueUpdateTasksPanel
      tasks={assetCustomFieldValueUpdateTasks}
      onCancel={cancelTask}
      onClear={cancelTask}
    />
  );
});

PrivateAssetCustomFieldValueUpdateTasksPanel.displayName = 'PrivateAssetCustomFieldValueUpdateTasksPanel';
